import { useState, useEffect, useCallback } from "react";
import { HashRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Car, Calendar, MapPin, Phone, Mail, Star, Check, ChevronRight, ChevronLeft, Menu, X, Shield, Clock, CreditCard, MessageCircle, Search, Settings, LogOut, Plus, Edit, Trash2, Image, ArrowLeft, FileText, ClipboardCheck, Eye } from "lucide-react";
import axios from "axios";
import "./index.css";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// ==================== SHARED COMPONENTS ====================

const Header = ({ isAdmin = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setMobileMenuOpen(false); }, [location]);

  const navLinks = [
    { to: "/vehicules", label: "Véhicules" },
    { to: "/comment-ca-marche", label: "Comment ça marche" },
    { to: "/avis", label: "Avis" },
    { to: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "header-glass" : "bg-[var(--background)]/80 backdrop-blur-md"}`} data-testid="header">
      <div className="container flex items-center justify-between h-[72px]">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <img src="https://static.prod-images.emergentagent.com/jobs/9fbc62be-9ca6-4a52-a262-7277632737f6/images/66bea74a5f4b92055b5bbbae51f5178c1fabf0694382f7474751b63e58c61714.png" alt="SIMCarRent" className="h-10" />
        </Link>
        {!isAdmin && (
          <>
            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map(link => (
                <Link key={link.to} to={link.to} className={`transition font-medium ${isActive(link.to) ? "text-[var(--primary)]" : "text-[var(--text-secondary)] hover:text-white"}`} data-testid={`nav-${link.to.slice(1)}`}>
                  {link.label}
                </Link>
              ))}
              <Link to="/admin" className="btn-primary text-sm py-2 px-5" data-testid="nav-admin">Admin</Link>
            </nav>
            <button className="md:hidden p-2 text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            {mobileMenuOpen && (
              <div className="mobile-menu md:hidden">
                <nav className="flex flex-col gap-4">
                  {navLinks.map(link => (
                    <Link key={link.to} to={link.to} className={`text-lg font-medium ${isActive(link.to) ? "text-[var(--primary)]" : ""}`}>{link.label}</Link>
                  ))}
                  <Link to="/admin" className="btn-primary text-center">Admin</Link>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    </header>
  );
};

const WhatsAppButton = () => (
  <a href="https://wa.me/221767481748" target="_blank" rel="noopener noreferrer" className="whatsapp-float" data-testid="whatsapp-btn">
    <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  </a>
);

const Footer = () => (
  <footer className="py-10 border-t border-[var(--border)] bg-[var(--surface)]" data-testid="footer">
    <div className="container">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        <div>
          <img src="https://static.prod-images.emergentagent.com/jobs/9fbc62be-9ca6-4a52-a262-7277632737f6/images/66bea74a5f4b92055b5bbbae51f5178c1fabf0694382f7474751b63e58c61714.png" alt="SIMCarRent" className="h-8 mb-3" />
          <p className="text-sm text-gray-400">Location de voitures à Dakar, Sénégal. Véhicules récents et bien entretenus.</p>
        </div>
        <div>
          <h4 className="font-bold text-white mb-3">Navigation</h4>
          <div className="flex flex-col gap-2">
            <Link to="/vehicules" className="text-sm text-gray-400 hover:text-[var(--primary)]">Véhicules</Link>
            <Link to="/comment-ca-marche" className="text-sm text-gray-400 hover:text-[var(--primary)]">Comment ça marche</Link>
            <Link to="/avis" className="text-sm text-gray-400 hover:text-[var(--primary)]">Avis</Link>
            <Link to="/contact" className="text-sm text-gray-400 hover:text-[var(--primary)]">Contact</Link>
          </div>
        </div>
        <div>
          <h4 className="font-bold text-white mb-3">Contact</h4>
          <div className="flex flex-col gap-2">
            <a href="tel:+221767481748" className="text-sm text-gray-400 hover:text-[var(--primary)]">+221 76 748 17 48</a>
            <a href="mailto:simcarrent@gmail.com" className="text-sm text-gray-400 hover:text-[var(--primary)]">simcarrent@gmail.com</a>
            <p className="text-sm text-gray-400">Ouest Foire, Lot 6 - Dakar VDN</p>
          </div>
        </div>
      </div>
      <div className="border-t border-[var(--border)] pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-sm text-gray-500">© 2025 SIMCarRent S.R.C - Dakar, Sénégal</p>
        <div className="flex gap-6">
          <a href="#" className="text-sm text-gray-500 hover:text-white">Conditions générales</a>
          <a href="#" className="text-sm text-gray-500 hover:text-white">Politique de confidentialité</a>
        </div>
      </div>
    </div>
  </footer>
);

const PageWrapper = ({ children }) => (
  <div className="min-h-screen bg-[var(--background)] flex flex-col">
    <Header />
    <main className="flex-1 pt-[72px]">{children}</main>
    <Footer />
    <WhatsAppButton />
  </div>
);

// Fallback image for broken URLs
const CarImage = ({ src, alt, className }) => {
  const [error, setError] = useState(false);
  if (error || !src) return (
    <div className={`${className} bg-[var(--surface-elevated)] flex flex-col items-center justify-center text-gray-500`}>
      <Car size={32} className="mb-1 opacity-30" />
      <span className="text-xs opacity-50">Photo manquante</span>
    </div>
  );
  return <img src={src} alt={alt} className={className} onError={() => setError(true)} />;
};

// Small image gallery for cards
const ImageGallerySmall = ({ images, alt }) => {
  const [idx, setIdx] = useState(0);
  const allImages = images?.filter(u => u)?.length > 0 ? images.filter(u => u) : [];
  if (allImages.length === 0) return <CarImage src={null} alt={alt} className="w-full h-full" />;
  if (allImages.length === 1) return <CarImage src={allImages[0]} alt={alt} className="w-full h-full object-cover" />;
  return (
    <div className="relative w-full h-full group">
      <CarImage src={allImages[idx]} alt={alt} className="w-full h-full object-cover" />
      <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); setIdx((idx - 1 + allImages.length) % allImages.length); }} className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"><ChevronLeft size={16} /></button>
      <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); setIdx((idx + 1) % allImages.length); }} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"><ChevronRight size={16} /></button>
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">{allImages.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === idx ? "bg-white w-3" : "bg-white/50"}`} />)}</div>
    </div>
  );
};

// Car card used on vehicules page and homepage
const CarCard = ({ car }) => {
  const navigate = useNavigate();
  const allImages = car.images?.length > 0 ? car.images : (car.image_url ? [car.image_url] : []);
  const getBadgeText = (b) => b === "most_requested" ? "Plus demandée" : b === "offer" ? "Offre" : null;
  const statusLabels = { available: "Disponible", rented: "Réservée", maintenance: "En maintenance" };
  const statusColors = { available: "badge-success", rented: "status-pending", maintenance: "status-cancelled" };
  const fuelLabels = { essence: "Essence", diesel: "Diesel", electrique: "Électrique", hybride: "Hybride" };
  const carStatus = car.status || "available";
  const isBookable = carStatus === "available";

  return (
    <div className={`card cursor-pointer ${!isBookable ? "opacity-70" : ""}`} onClick={() => navigate(`/car/${car.id}`)} data-testid={`car-card-${car.id}`}>
      <div className="relative h-52 bg-[var(--surface-elevated)]">
        <ImageGallerySmall images={allImages} alt={`${car.brand} ${car.model}`} />
        {!isBookable && <div className="absolute inset-0 bg-black/30" />}
        {car.badge && isBookable && <span className="badge badge-primary absolute top-3 right-3 z-10">{getBadgeText(car.badge)}</span>}
        <span className={`badge ${statusColors[carStatus]} absolute top-3 ${car.badge && isBookable ? "left-3" : "right-3"} z-10`}>{statusLabels[carStatus]}</span>
        {allImages.length > 1 && <span className="absolute bottom-3 left-3 z-10 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1"><Image size={12} /> {allImages.length}</span>}
      </div>
      <div className="p-5">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h3 className="font-bold text-lg text-white">{car.brand} {car.model}</h3>
            <p className="text-sm text-gray-400">{car.year} • {car.transmission === "automatic" ? "Automatique" : "Manuelle"} • {fuelLabels[car.fuel_type] || "Essence"} • {car.seats || 5} places</p>
          </div>
          <div className="text-right">
            <p className="price-tag text-xl">{car.daily_price?.toLocaleString()} FCFA</p>
            <p className="text-xs text-gray-400">par jour</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 my-4">
          {car.features?.slice(0, 3).map((f, i) => <span key={i} className="feature-chip">{f}</span>)}
        </div>
        <Link to={`/car/${car.id}`} className={`${isBookable ? "btn-primary" : "btn-secondary"} w-full block text-center`} onClick={(e) => e.stopPropagation()} data-testid={`view-btn-${car.id}`}>
          {isBookable ? "Voir détails" : "Voir fiche"}
        </Link>
        {!isBookable && car.available_from && (
          <p className="text-xs text-center mt-2 text-gray-400">Disponible dès le <span className="text-[var(--primary)] font-medium">{new Date(car.available_from).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span></p>
        )}
      </div>
    </div>
  );
};

// ==================== PAGE: HOME ====================

const HomePage = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.post(`${API_URL}/api/seed`).catch(() => {});
    axios.get(`${API_URL}/api/cars`).then(r => setCars(r.data.cars)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <PageWrapper>
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/9fbc62be-9ca6-4a52-a262-7277632737f6/images/5a1dfc851f24dc47e30c659807f9269e10d43729c8db5c8e42e0c0e1703eee74.png')` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/50 via-[#0A0A0B]/40 to-[#0A0A0B]" />
        <div className="container relative z-10">
          <div className="max-w-2xl animate-fadeInUp">
            <span className="inline-block bg-[var(--primary)] text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-5">Location de voiture à Dakar</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight text-white">Louez votre voiture <span className="text-[var(--primary)]">en toute simplicité</span></h1>
            <p className="text-base md:text-lg text-gray-300 mb-8 leading-relaxed">Des véhicules récents et bien entretenus. Paiement sécurisé via Wave ou Orange Money. Livraison à domicile ou à l'aéroport.</p>
            <Link to="/vehicules" className="btn-primary inline-flex items-center gap-2 text-lg py-4 px-8" data-testid="cta-vehicules">
              <Search size={20} /> Voir nos véhicules
            </Link>
          </div>
        </div>
      </section>

      {/* Preview cars */}
      <section className="section bg-[var(--background)]">
        <div className="container">
          <div className="flex justify-between items-end mb-8">
            <div>
              <span className="inline-block bg-[var(--primary)] text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">Nos Véhicules</span>
              <h2 className="section-title text-white">Véhicules populaires</h2>
            </div>
            <Link to="/vehicules" className="btn-secondary hidden sm:inline-flex items-center gap-2">Voir tout <ChevronRight size={16} /></Link>
          </div>
          {loading ? <div className="flex justify-center py-12"><div className="spinner" /></div> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cars.slice(0, 3).map(car => <CarCard key={car.id} car={car} />)}
            </div>
          )}
          <div className="text-center mt-8 sm:hidden">
            <Link to="/vehicules" className="btn-primary inline-flex items-center gap-2">Voir tous les véhicules <ChevronRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* Why choose us preview */}
      <section className="section bg-[var(--surface)]">
        <div className="container">
          <div className="text-center mb-12">
            <span className="inline-block bg-[var(--primary)] text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">Pourquoi nous choisir</span>
            <h2 className="section-title text-white">La confiance avant tout</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Clock size={28} />, title: "Réservation rapide", desc: "Confirmation en moins de 5 minutes, 7j/7" },
              { icon: <Shield size={28} />, title: "Véhicules contrôlés", desc: "Tous nos véhicules sont régulièrement entretenus" },
              { icon: <CreditCard size={28} />, title: "Paiement flexible", desc: "Wave, Orange Money - paiement à la livraison" },
              { icon: <MessageCircle size={28} />, title: "Assistance WhatsApp", desc: "Support disponible 24h/24 via WhatsApp" },
            ].map((f, i) => (
              <div key={i} className="card p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-[var(--primary)]/20 flex items-center justify-center mx-auto mb-4 text-[var(--primary)]">{f.icon}</div>
                <h3 className="font-bold mb-2 text-white">{f.title}</h3>
                <p className="text-sm text-gray-400">{f.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/comment-ca-marche" className="btn-secondary inline-flex items-center gap-2">En savoir plus <ChevronRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-[var(--background)]">
        <div className="container text-center">
          <h2 className="section-title text-white mb-4">Prêt à louer ?</h2>
          <p className="text-gray-400 mb-8 max-w-lg mx-auto">Parcourez notre catalogue, choisissez votre véhicule et réservez en quelques clics.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/vehicules" className="btn-primary inline-flex items-center justify-center gap-2 text-lg py-4 px-8"><Car size={20} /> Voir les véhicules</Link>
            <a href="https://wa.me/221767481748" target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center justify-center gap-2 text-lg py-4 px-8"><MessageCircle size={20} /> WhatsApp</a>
          </div>
        </div>
      </section>
    </PageWrapper>
  );
};

// ==================== PAGE: VEHICULES ====================

const VehiculesPage = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const fetchCars = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.car_type) params.append("car_type", filters.car_type);
      if (filters.transmission) params.append("transmission", filters.transmission);
      if (filters.max_price) params.append("max_price", filters.max_price);
      if (filters.status) params.append("status", filters.status);
      const res = await axios.get(`${API_URL}/api/cars?${params}`);
      setCars(res.data.cars);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchCars(); }, [fetchCars]);

  const filteredCars = searchQuery ? cars.filter(c => `${c.brand} ${c.model}`.toLowerCase().includes(searchQuery.toLowerCase())) : cars;
  const availableCount = cars.filter(c => c.status === "available").length;

  return (
    <PageWrapper>
      <section className="section" data-testid="vehicules-page">
        <div className="container">
          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-black text-white mb-3">Nos Véhicules</h1>
            <p className="text-gray-400">{cars.length} véhicules au total, {availableCount} disponibles.</p>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" className="input pl-11 w-full" placeholder="Rechercher (ex: Toyota, Mercedes, Jeep...)" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input" />
              {searchQuery && <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"><X size={16} /></button>}
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 mb-8" data-testid="filters">
            <select className="select min-w-[160px] sm:w-auto" value={filters.status || ""} onChange={(e) => setFilters({ ...filters, status: e.target.value || null })} data-testid="filter-status">
              <option value="">Toutes disponibilités</option>
              <option value="available">Disponible</option>
              <option value="rented">Réservée</option>
              <option value="maintenance">En maintenance</option>
            </select>
            <select className="select min-w-[160px] sm:w-auto" value={filters.car_type || ""} onChange={(e) => setFilters({ ...filters, car_type: e.target.value || null })} data-testid="filter-type">
              <option value="">Tous les types</option><option value="economique">Économique</option><option value="suv">SUV</option><option value="luxe">Luxe</option>
            </select>
            <select className="select min-w-[160px] sm:w-auto" value={filters.transmission || ""} onChange={(e) => setFilters({ ...filters, transmission: e.target.value || null })} data-testid="filter-transmission">
              <option value="">Toutes transmissions</option><option value="manual">Manuelle</option><option value="automatic">Automatique</option>
            </select>
            <select className="select min-w-[160px] sm:w-auto" value={filters.max_price || ""} onChange={(e) => setFilters({ ...filters, max_price: e.target.value ? parseInt(e.target.value) : null })} data-testid="filter-price">
              <option value="">Tous les prix</option><option value="30000">≤ 30 000 FCFA</option><option value="50000">≤ 50 000 FCFA</option><option value="70000">≤ 70 000 FCFA</option><option value="100000">≤ 100 000 FCFA</option>
            </select>
          </div>

          {loading ? <div className="flex justify-center py-20"><div className="spinner" /></div> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCars.map(car => <CarCard key={car.id} car={car} />)}
              {filteredCars.length === 0 && <p className="col-span-full text-center text-gray-400 py-10">Aucun véhicule trouvé.</p>}
            </div>
          )}
        </div>
      </section>
    </PageWrapper>
  );
};

// ==================== PAGE: COMMENT CA MARCHE ====================

const CommentCaMarchePage = () => {
  const steps = [
    { num: 1, title: "Choisissez votre voiture", desc: "Parcourez notre catalogue en ligne. Utilisez les filtres pour trouver le véhicule idéal selon votre budget et vos besoins. SUV, berline, minivan — nous avons ce qu'il vous faut.", icon: <Search size={32} /> },
    { num: 2, title: "Réservez en ligne", desc: "Remplissez le formulaire de réservation avec vos informations personnelles, vos dates de location, et téléchargez votre pièce d'identité ou permis de conduire.", icon: <Calendar size={32} /> },
    { num: 3, title: "Confirmation rapide", desc: "Notre équipe vous contacte par WhatsApp dans les 5 minutes pour confirmer votre réservation et finaliser les détails (lieu de livraison, horaire).", icon: <MessageCircle size={32} /> },
    { num: 4, title: "Récupérez les clés", desc: "Rendez-vous en agence à Ouest Foire (Lot 6, Dakar VDN) ou recevez le véhicule directement à votre domicile ou à l'aéroport.", icon: <Car size={32} /> },
  ];

  return (
    <PageWrapper>
      <section className="section" data-testid="comment-ca-marche-page">
        <div className="container">
          <div className="text-center mb-16">
            <span className="inline-block bg-[var(--primary)] text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">Comment ça marche</span>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-3">Louez en 4 étapes simples</h1>
            <p className="text-gray-400 max-w-xl mx-auto">De la recherche à la prise en main, tout est pensé pour être rapide et facile.</p>
          </div>

          <div className="max-w-3xl mx-auto space-y-8">
            {steps.map((step) => (
              <div key={step.num} className="flex gap-6 items-start" data-testid={`step-${step.num}`}>
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-2xl bg-[var(--primary)] flex items-center justify-center text-white">{step.icon}</div>
                </div>
                <div className="flex-1 pb-8 border-b border-[var(--border)] last:border-0">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-[var(--primary)] font-black text-sm">ÉTAPE {step.num}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link to="/vehicules" className="btn-primary inline-flex items-center gap-2 text-lg py-4 px-8"><Car size={20} /> Voir les véhicules</Link>
          </div>
        </div>
      </section>
    </PageWrapper>
  );
};

// ==================== PAGE: AVIS ====================

const AvisPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: "", location: "", text: "", stars: 5 });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hoverStar, setHoverStar] = useState(0);

  useEffect(() => {
    axios.get(`${API_URL}/api/reviews`).then(r => setReviews(r.data.reviews)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/reviews`, formData);
      setSubmitted(true);
      setShowForm(false);
      setFormData({ name: "", location: "", text: "", stars: 5 });
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <section className="section" data-testid="avis-page">
        <div className="container">
          <div className="text-center mb-12">
            <span className="inline-block bg-[var(--primary)] text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">Témoignages</span>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-3">Ce que disent nos clients</h1>
            <p className="text-gray-400 max-w-xl mx-auto">La satisfaction de nos clients est notre priorité. Découvrez leurs expériences.</p>
          </div>

          {/* Submit review button/form */}
          {submitted ? (
            <div className="max-w-lg mx-auto mb-12 bg-green-500/10 border border-green-500/30 rounded-xl p-6 text-center" data-testid="review-submitted">
              <Check size={32} className="text-green-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-white mb-1">Merci pour votre avis !</h3>
              <p className="text-gray-400 text-sm">Votre avis sera publié après vérification.</p>
            </div>
          ) : !showForm ? (
            <div className="text-center mb-12">
              <button className="btn-primary inline-flex items-center gap-2" onClick={() => setShowForm(true)} data-testid="write-review-btn">
                <Star size={18} /> Laisser un avis
              </button>
            </div>
          ) : (
            <div className="max-w-lg mx-auto mb-12 bg-[var(--surface)] rounded-xl p-6 border border-[var(--border)]" data-testid="review-form">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-white">Votre avis</h3>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="label">Votre nom</label>
                    <input type="text" className="input" required placeholder="Ex: Mamadou D." value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="review-name" />
                  </div>
                  <div>
                    <label className="label">Ville / Pays</label>
                    <input type="text" className="input" required placeholder="Ex: Dakar" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} data-testid="review-location" />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="label">Note</label>
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map(s => (
                      <button key={s} type="button" onMouseEnter={() => setHoverStar(s)} onMouseLeave={() => setHoverStar(0)} onClick={() => setFormData({ ...formData, stars: s })} className="p-1 transition" data-testid={`star-${s}`}>
                        <Star size={28} className={`${(hoverStar || formData.stars) >= s ? "text-yellow-500" : "text-gray-600"} transition-colors`} fill={(hoverStar || formData.stars) >= s ? "currentColor" : "none"} />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mb-4">
                  <label className="label">Votre avis</label>
                  <textarea className="input min-h-[100px]" required placeholder="Partagez votre expérience avec SIMCarRent..." value={formData.text} onChange={(e) => setFormData({ ...formData, text: e.target.value })} data-testid="review-text" />
                </div>
                <button type="submit" className="btn-primary w-full" disabled={submitting} data-testid="submit-review-btn">
                  {submitting ? <div className="spinner mx-auto" /> : "Envoyer mon avis"}
                </button>
              </form>
            </div>
          )}

          {/* Reviews list */}
          {loading ? <div className="flex justify-center py-12"><div className="spinner" /></div> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review) => (
                <div key={review.id} className="review-card" data-testid={`review-${review.id}`}>
                  <div className="flex gap-1 mb-3 text-yellow-500">{[...Array(review.stars)].map((_, j) => <Star key={j} size={16} fill="currentColor" />)}</div>
                  <p className="text-gray-300 mb-4 leading-relaxed">"{review.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[var(--primary)]/20 flex items-center justify-center text-[var(--primary)] font-bold text-sm">{review.name.charAt(0)}</div>
                    <div>
                      <p className="font-bold text-sm text-white">{review.name}</p>
                      <p className="text-xs text-gray-400">{review.location}</p>
                    </div>
                  </div>
                </div>
              ))}
              {reviews.length === 0 && <p className="col-span-full text-center text-gray-400 py-10">Pas encore d'avis. Soyez le premier !</p>}
            </div>
          )}
        </div>
      </section>
    </PageWrapper>
  );
};

// ==================== PAGE: CONTACT ====================

const ContactPage = () => (
  <PageWrapper>
    <section className="section" data-testid="contact-page">
      <div className="container">
        <div className="text-center mb-12">
          <span className="inline-block bg-[var(--primary)] text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">Contact</span>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3">Contactez-nous</h1>
          <p className="text-gray-400 max-w-xl mx-auto">Une question ? Besoin d'un devis ? Contactez-nous par téléphone, email ou WhatsApp.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            {[
              { icon: <Phone size={24} />, title: "WhatsApp / Téléphone", content: <><a href="tel:+221767481748" className="text-[var(--primary)] font-medium text-lg">+221 76 748 17 48</a><p className="text-sm text-gray-400 mt-1">Disponible 7j/7, 8h - 22h</p></>, cta: <a href="https://wa.me/221767481748" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2 mt-3 text-sm py-2 px-4"><MessageCircle size={16} /> Écrire sur WhatsApp</a> },
              { icon: <Mail size={24} />, title: "Email", content: <a href="mailto:simcarrent@gmail.com" className="text-[var(--primary)] font-medium">simcarrent@gmail.com</a> },
              { icon: <MapPin size={24} />, title: "Adresse", content: <><p className="text-gray-300">Ouest Foire, Lot 6</p><p className="text-gray-400">Dakar VDN, Sénégal</p></> },
              { icon: <Clock size={24} />, title: "Horaires", content: <><p className="text-gray-300">Lundi - Dimanche</p><p className="text-gray-400">8h00 - 22h00</p></> },
            ].map((item, i) => (
              <div key={i} className="flex gap-5">
                <div className="w-14 h-14 rounded-xl bg-[var(--surface)] flex items-center justify-center text-[var(--primary)] flex-shrink-0">{item.icon}</div>
                <div>
                  <h3 className="font-bold text-white mb-1">{item.title}</h3>
                  {item.content}
                  {item.cta}
                </div>
              </div>
            ))}
          </div>
          <div className="h-[400px] rounded-xl overflow-hidden bg-[var(--surface)]">
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3858.8!2d-17.45!3d14.72!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTTCsDQzJzEyLjAiTiAxN8KwMjcnMDAuMCJX!5e0!3m2!1sfr!2ssn!4v1" width="100%" height="100%" style={{ border: 0, filter: "grayscale(100%) invert(92%) contrast(83%)" }} allowFullScreen="" loading="lazy" title="SIMCarRent Location" />
          </div>
        </div>
      </div>
    </section>
  </PageWrapper>
);

// ==================== PAGE: CAR DETAIL ====================

const CarDetailPage = () => {
  const { carId } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showBooking, setShowBooking] = useState(false);
  const [formData, setFormData] = useState({ first_name: "", last_name: "", phone: "", email: "", start_date: "", end_date: "", pickup_type: "depot", pickup_address: "", with_driver: "" });
  const [docFront, setDocFront] = useState(null);
  const [docBack, setDocBack] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/cars/${carId}`).then(r => setCar(r.data)).catch(() => navigate("/vehicules")).finally(() => setLoading(false));
  }, [carId, navigate]);

  const allImages = car?.images?.length > 0 ? car.images : (car?.image_url ? [car.image_url] : []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      let docFrontBase64 = null;
      let docBackBase64 = null;
      if (docFront) { const r = new FileReader(); docFrontBase64 = await new Promise(res => { r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(docFront); }); }
      if (docBack) { const r = new FileReader(); docBackBase64 = await new Promise(res => { r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(docBack); }); }
      const response = await axios.post(`${API_URL}/api/bookings`, { ...formData, car_id: car.id, document_front_base64: docFrontBase64, document_back_base64: docBackBase64, pickup_address: formData.pickup_type === "custom" ? formData.pickup_address : null });
      setSuccess(response.data);
    } catch (err) { setError(err.response?.data?.detail || "Une erreur est survenue"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <PageWrapper><div className="flex justify-center py-32"><div className="spinner" /></div></PageWrapper>;
  if (!car) return null;

  if (success) {
    return (
      <PageWrapper>
        <div className="container py-12">
          <div className="max-w-lg mx-auto text-center glass rounded-xl p-8">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4"><Check size={32} className="text-green-500" /></div>
            <h2 className="text-2xl font-bold mb-2 text-white">Réservation créée !</h2>
            <p className="text-gray-400 mb-2">Nous vous contacterons par WhatsApp pour confirmer.</p>
            <p className="text-gray-400 mb-2">Numéro: <span className="text-[var(--primary)] font-bold">{success.booking.booking_id}</span></p>
            <p className="text-gray-400 mb-6">Lieu: <span className="text-white font-medium">{success.booking.pickup_display}</span></p>
            <div className="bg-[var(--surface)] rounded-lg p-4 mb-6 text-left">
              <h4 className="font-bold mb-2 text-white">Informations de paiement</h4>
              <p className="text-sm text-gray-400 mb-3">{success.payment_info.instructions}</p>
              {success.payment_info.payment_methods.map((m, i) => <div key={i} className="flex justify-between items-center mb-1"><span className="text-white">{m.name}</span><span className="text-[var(--primary)]">{m.number}</span></div>)}
              <div className="mt-3 pt-3 border-t border-[var(--border)]">
                <div className="flex justify-between text-sm text-gray-400"><span>Total</span><span className="text-white font-bold">{success.booking.total_price?.toLocaleString()} FCFA</span></div>
              </div>
            </div>
            <div className="flex gap-3">
              <Link to="/vehicules" className="btn-secondary flex-1 text-center">Véhicules</Link>
              <a href="https://wa.me/221767481748" target="_blank" rel="noopener noreferrer" className="btn-primary flex-1 flex items-center justify-center gap-2"><MessageCircle size={16} /> WhatsApp</a>
            </div>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="container py-8" data-testid="car-detail-page">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition" data-testid="back-btn">
          <ArrowLeft size={18} /> Retour
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Gallery */}
          <div>
            <div className="rounded-xl overflow-hidden bg-[var(--surface-elevated)] h-[350px] md:h-[420px] mb-3">
              <CarImage src={allImages[selectedImage]} alt={`${car.brand} ${car.model}`} className="w-full h-full object-cover" />
            </div>
            {allImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {allImages.map((img, i) => (
                  <button key={i} onClick={() => setSelectedImage(i)} className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition ${i === selectedImage ? "border-[var(--primary)]" : "border-transparent opacity-60 hover:opacity-100"}`}>
                    <img src={img} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            {car.badge && <span className="inline-block bg-[var(--primary)] text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">{car.badge === "most_requested" ? "Plus demandée" : "Offre"}</span>}
            <h1 className="text-3xl md:text-4xl font-black text-white mb-2">{car.brand} {car.model}</h1>
            <div className="flex items-center gap-3 mb-6">
              <p className="text-gray-400">{car.year} • {car.car_type?.charAt(0).toUpperCase() + car.car_type?.slice(1)} • {car.transmission === "automatic" ? "Automatique" : "Manuelle"} • {{ essence: "Essence", diesel: "Diesel", electrique: "Électrique", hybride: "Hybride" }[car.fuel_type] || "Essence"} • {car.seats || 5} places{car.mileage ? ` • ${car.mileage.toLocaleString()} km` : ""}</p>
              <span className={`badge ${car.status === "available" ? "badge-success" : car.status === "rented" ? "status-pending" : "status-cancelled"}`}>
                {car.status === "available" ? "Disponible" : car.status === "rented" ? "Réservée" : "En maintenance"}
              </span>
            </div>

            <div className="bg-[var(--surface)] rounded-xl p-5 mb-6 border border-[var(--border)]">
              <span className="text-3xl font-black text-[var(--primary)]">{car.daily_price?.toLocaleString()}</span>
              <span className="text-gray-400 ml-2">FCFA / jour</span>
            </div>

            {car.description && <div className="mb-6"><h3 className="font-bold text-white mb-2">Description</h3><p className="text-gray-400 leading-relaxed">{car.description}</p></div>}

            <div className="mb-6">
              <h3 className="font-bold text-white mb-3">Caractéristiques</h3>
              <div className="flex flex-wrap gap-2">
                {car.features?.map((f, i) => <span key={i} className="inline-flex items-center gap-1.5 px-3 py-2 bg-[var(--surface-elevated)] border border-[var(--border)] rounded-lg text-sm text-gray-300"><Check size={14} className="text-[var(--primary)]" /> {f}</span>)}
              </div>
            </div>

            {!showBooking ? (
              car.status === "available" ? (
                <button className="btn-primary w-full text-lg py-4" onClick={() => setShowBooking(true)} data-testid="open-booking-btn">Réserver ce véhicule</button>
              ) : (
                <div className="bg-[var(--surface)] rounded-xl p-5 border border-[var(--border)] text-center">
                  <p className="text-gray-400 mb-2">{car.status === "rented" ? "Ce véhicule est actuellement en location." : "Ce véhicule est en maintenance."}</p>
                  {car.available_from && <p className="text-white mb-3">Disponible dès le <span className="text-[var(--primary)] font-bold">{new Date(car.available_from).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span></p>}
                  <a href="https://wa.me/221767481748" target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center gap-2"><MessageCircle size={16} /> Contactez-nous</a>
                </div>
              )
            ) : (
              <div className="bg-[var(--surface)] rounded-xl p-6 border border-[var(--border)]" data-testid="booking-form">
                <h3 className="text-lg font-bold text-white mb-4">Formulaire de réservation</h3>
                {error && <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 mb-4 text-red-400 text-sm">{error}</div>}
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div><label className="label">Prénom</label><input type="text" className="input" required value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} data-testid="booking-first-name" /></div>
                    <div><label className="label">Nom</label><input type="text" className="input" required value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} data-testid="booking-last-name" /></div>
                  </div>
                  <div className="mb-3"><label className="label">Téléphone</label><input type="tel" className="input" required placeholder="+221 77 XXX XX XX" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="booking-phone" /></div>
                  <div className="mb-3"><label className="label">Email</label><input type="email" className="input" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="booking-email" /></div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div><label className="label">Date début</label><input type="date" className="input" required value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} data-testid="booking-start-date" /></div>
                    <div><label className="label">Date fin</label><input type="date" className="input" required value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} data-testid="booking-end-date" /></div>
                  </div>

                  {/* Pickup location */}
                  <div className="mb-3">
                    <label className="label">Lieu de retrait / livraison</label>
                    <div className="space-y-2">
                      <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${formData.pickup_type === "depot" ? "border-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] hover:border-gray-500"}`}>
                        <input type="radio" name="pickup" value="depot" checked={formData.pickup_type === "depot"} onChange={(e) => setFormData({ ...formData, pickup_type: e.target.value })} className="accent-[var(--primary)]" data-testid="pickup-depot" />
                        <MapPin size={18} className="text-[var(--primary)]" />
                        <div>
                          <p className="text-white text-sm font-medium">Retrait au dépôt</p>
                          <p className="text-gray-400 text-xs">Ouest Foire, Lot 6 - Dakar VDN</p>
                        </div>
                      </label>
                      <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${formData.pickup_type === "airport" ? "border-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] hover:border-gray-500"}`}>
                        <input type="radio" name="pickup" value="airport" checked={formData.pickup_type === "airport"} onChange={(e) => setFormData({ ...formData, pickup_type: e.target.value })} className="accent-[var(--primary)]" data-testid="pickup-airport" />
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" className="text-[var(--primary)]"><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/></svg>
                        <div>
                          <p className="text-white text-sm font-medium">Livraison à l'aéroport</p>
                          <p className="text-gray-400 text-xs">Aéroport Blaise Diagne (AIBD)</p>
                        </div>
                      </label>
                      <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${formData.pickup_type === "custom" ? "border-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] hover:border-gray-500"}`}>
                        <input type="radio" name="pickup" value="custom" checked={formData.pickup_type === "custom"} onChange={(e) => setFormData({ ...formData, pickup_type: e.target.value })} className="accent-[var(--primary)]" data-testid="pickup-custom" />
                        <MapPin size={18} className="text-[var(--primary)]" />
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium">Adresse personnalisée</p>
                          <p className="text-gray-400 text-xs">Livraison à votre adresse</p>
                        </div>
                      </label>
                    </div>
                    {formData.pickup_type === "custom" && (
                      <input type="text" className="input mt-2" required placeholder="Entrez l'adresse de livraison..." value={formData.pickup_address} onChange={(e) => setFormData({ ...formData, pickup_address: e.target.value })} data-testid="pickup-address" />
                    )}
                  </div>

                  {/* With/Without driver */}
                  <div className="mb-3">
                    <label className="label">Option chauffeur</label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${formData.with_driver === "no" ? "border-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] hover:border-gray-500"}`}>
                        <input type="radio" name="driver" value="no" required checked={formData.with_driver === "no"} onChange={(e) => setFormData({ ...formData, with_driver: e.target.value })} className="accent-[var(--primary)]" data-testid="driver-no" />
                        <div>
                          <p className="text-white text-sm font-medium">Sans chauffeur</p>
                          <p className="text-gray-400 text-xs">Je conduis moi-même</p>
                        </div>
                      </label>
                      <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${formData.with_driver === "yes" ? "border-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] hover:border-gray-500"}`}>
                        <input type="radio" name="driver" value="yes" required checked={formData.with_driver === "yes"} onChange={(e) => setFormData({ ...formData, with_driver: e.target.value })} className="accent-[var(--primary)]" data-testid="driver-yes" />
                        <div>
                          <p className="text-white text-sm font-medium">Avec chauffeur</p>
                          <p className="text-gray-400 text-xs">Chauffeur professionnel</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="label">Permis de conduire - Recto (avant)</label>
                    <input type="file" className="input" required accept="image/*,.pdf" onChange={(e) => setDocFront(e.target.files[0])} data-testid="booking-doc-front" />
                  </div>
                  <div className="mb-4">
                    <label className="label">Permis de conduire - Verso (arrière)</label>
                    <input type="file" className="input" required accept="image/*,.pdf" onChange={(e) => setDocBack(e.target.files[0])} data-testid="booking-doc-back" />
                  </div>
                  <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={submitting} data-testid="booking-submit-btn">{submitting ? <div className="spinner" /> : "Confirmer la réservation"}</button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

// ==================== ADMIN PAGES ====================

const AdminLoginPage = () => {
  const [username, setUsername] = useState(""); const [password, setPassword] = useState(""); const [error, setError] = useState(""); const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try { const r = await axios.post(`${API_URL}/api/admin/login`, { username, password }); if (r.data.success) { localStorage.setItem("admin_logged_in", "true"); localStorage.setItem("admin_username", r.data.username); navigate("/admin/dashboard"); } }
    catch (err) { setError(err.response?.data?.detail || "Identifiants incorrects"); } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[var(--background)]" data-testid="admin-login-page">
      <div className="glass rounded-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <img src="https://static.prod-images.emergentagent.com/jobs/9fbc62be-9ca6-4a52-a262-7277632737f6/images/66bea74a5f4b92055b5bbbae51f5178c1fabf0694382f7474751b63e58c61714.png" alt="SIMCarRent" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Espace Administrateur</h1>
          <p className="text-gray-400">Connectez-vous pour gérer le site</p>
        </div>
        {error && <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 mb-4 text-red-400 text-sm">{error}</div>}
        <form onSubmit={handleLogin}>
          <div className="mb-4"><label className="label">Identifiant</label><input type="text" className="input" value={username} onChange={(e) => setUsername(e.target.value)} data-testid="admin-username" /></div>
          <div className="mb-6"><label className="label">Mot de passe</label><input type="password" className="input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" /></div>
          <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="admin-login-btn">{loading ? <div className="spinner mx-auto" /> : "Se connecter"}</button>
        </form>
        <div className="mt-6 text-center"><Link to="/" className="text-sm text-gray-400 hover:text-white">← Retour au site</Link></div>
      </div>
    </div>
  );
};

const MultiImageInput = ({ images, onChange }) => {
  const fileInputRef = { current: null };
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (images.length + files.length > 10) {
      alert("Maximum 10 photos");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append("files", f));
      const res = await axios.post(`${API_URL}/api/upload/multiple`, formData);
      const fullUrls = res.data.urls.map(u => `${API_URL}${u}`);
      onChange([...images, ...fullUrls]);
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Erreur lors de l'upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => onChange(images.filter((_, i) => i !== idx));

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="label mb-0">Photos ({images.filter(u => u).length}/10)</label>
      </div>

      {/* Preview grid */}
      {images.filter(u => u).length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {images.filter(u => u).map((url, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden bg-[var(--surface-elevated)] aspect-video">
              <img src={url} alt={`Photo ${i+1}`} className="w-full h-full object-cover" onError={(e) => { e.target.src = ""; }} />
              <button type="button" onClick={() => removeImage(images.indexOf(url))} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X size={12} />
              </button>
              {i === 0 && <span className="absolute bottom-1 left-1 bg-[var(--primary)] text-white text-[10px] px-1.5 py-0.5 rounded">Principale</span>}
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      {images.filter(u => u).length < 10 && (
        <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-[var(--border)] rounded-xl cursor-pointer hover:border-[var(--primary)] hover:bg-[var(--primary)]/5 transition ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
          <input type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={handleFileSelect} className="hidden" />
          {uploading ? (
            <div className="flex items-center gap-2 text-gray-400"><div className="spinner" /> Upload en cours...</div>
          ) : (
            <>
              <Plus size={24} className="text-[var(--primary)] mb-1" />
              <span className="text-sm text-gray-400">Cliquez ou glissez vos photos ici</span>
              <span className="text-xs text-gray-500">JPG, PNG, WEBP • Max 10 photos</span>
            </>
          )}
        </label>
      )}

      {/* Manual URL option */}
      <details className="mt-2">
        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-400">Ajouter par URL</summary>
        <div className="mt-2 flex gap-2">
          <input type="url" id="manual-url" className="input flex-1 text-sm" placeholder="https://..." />
          <button type="button" className="btn-secondary text-sm py-2 px-3" onClick={() => {
            const input = document.getElementById("manual-url");
            if (input.value && images.length < 10) { onChange([...images, input.value]); input.value = ""; }
          }}>Ajouter</button>
        </div>
      </details>
    </div>
  );
};

const CarModal = ({ car, onClose, onSave }) => {
  const [formData, setFormData] = useState({ brand: car?.brand || "", model: car?.model || "", year: car?.year || new Date().getFullYear(), daily_price: car?.daily_price || "", description: car?.description || "", car_type: car?.car_type || "suv", transmission: car?.transmission || "automatic", fuel_type: car?.fuel_type || "essence", seats: car?.seats || 5, mileage: car?.mileage || "", badge: car?.badge || "", status: car?.status || "available", available_from: car?.available_from || "", is_available: car?.is_available !== undefined ? car.is_available : true });
  const [images, setImages] = useState(car?.images?.length > 0 ? [...car.images] : (car?.image_url ? [car.image_url] : [""]));
  const [featuresInput, setFeaturesInput] = useState(car?.features?.join(", ") || "");
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    const validImages = images.filter(u => u.trim());
    const data = { ...formData, features: featuresInput.split(",").map(f => f.trim()).filter(f => f), daily_price: parseInt(formData.daily_price), seats: parseInt(formData.seats) || 5, mileage: formData.mileage ? parseInt(formData.mileage) : null, images: validImages, image_url: validImages[0] || "", badge: formData.badge || null, is_available: formData.status === "available" };
    try { if (car) { await axios.put(`${API_URL}/api/admin/cars/${car.id}`, data); } else { await axios.post(`${API_URL}/api/admin/cars`, data); } onSave(); } catch (error) { console.error(error); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" data-testid="car-modal">
      <div className="glass rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6"><h3 className="text-xl font-bold text-white">{car ? "Modifier" : "Ajouter"}</h3><button onClick={onClose} className="p-2 hover:bg-[var(--surface)] rounded-lg"><X size={20} /></button></div>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4 mb-4"><div><label className="label">Marque</label><input type="text" className="input" required value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} /></div><div><label className="label">Modèle</label><input type="text" className="input" required value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} /></div></div>
          <div className="grid grid-cols-2 gap-4 mb-4"><div><label className="label">Année</label><input type="number" className="input" required value={formData.year} onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })} /></div><div><label className="label">Prix/jour (FCFA)</label><input type="number" className="input" required value={formData.daily_price} onChange={(e) => setFormData({ ...formData, daily_price: e.target.value })} /></div></div>
          <div className="grid grid-cols-2 gap-4 mb-4"><div><label className="label">Type</label><select className="select w-full" value={formData.car_type} onChange={(e) => setFormData({ ...formData, car_type: e.target.value })}><option value="economique">Économique</option><option value="suv">SUV</option><option value="luxe">Luxe</option></select></div><div><label className="label">Transmission</label><select className="select w-full" value={formData.transmission} onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}><option value="manual">Manuelle</option><option value="automatic">Automatique</option></select></div></div>
          <div className="grid grid-cols-3 gap-4 mb-4"><div><label className="label">Carburant</label><select className="select w-full" value={formData.fuel_type} onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })}><option value="essence">Essence</option><option value="diesel">Diesel</option><option value="electrique">Électrique</option><option value="hybride">Hybride</option></select></div><div><label className="label">Places</label><input type="number" className="input" min="2" max="12" value={formData.seats} onChange={(e) => setFormData({ ...formData, seats: e.target.value })} /></div><div><label className="label">Kilométrage</label><input type="number" className="input" placeholder="km" value={formData.mileage} onChange={(e) => setFormData({ ...formData, mileage: e.target.value })} /></div></div>
          <div className="mb-4"><MultiImageInput images={images} onChange={setImages} /></div>
          <div className="mb-4"><label className="label">Description</label><textarea className="input min-h-[80px]" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
          <div className="mb-4"><label className="label">Équipements (virgule)</label><input type="text" className="input" placeholder="Climatisation, GPS..." value={featuresInput} onChange={(e) => setFeaturesInput(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-4 mb-4"><div><label className="label">Badge</label><select className="select w-full" value={formData.badge || ""} onChange={(e) => setFormData({ ...formData, badge: e.target.value || null })}><option value="">Aucun</option><option value="most_requested">Plus demandée</option><option value="offer">Offre</option></select></div><div><label className="label">Statut</label><select className="select w-full" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}><option value="available">Disponible</option><option value="rented">Réservée</option><option value="maintenance">En maintenance</option></select></div></div>
          {formData.status !== "available" && (
            <div className="mb-4"><label className="label">Disponible à partir du</label><input type="date" className="input" value={formData.available_from} onChange={(e) => setFormData({ ...formData, available_from: e.target.value })} /></div>
          )}
          <button type="submit" className="btn-primary w-full" disabled={loading}>{loading ? <div className="spinner mx-auto" /> : (car ? "Mettre à jour" : "Ajouter")}</button>
        </form>
      </div>
    </div>
  );
};

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null); const [bookings, setBookings] = useState([]); const [cars, setCars] = useState([]); const [reviews, setReviews] = useState([]); const [contracts, setContracts] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard"); const [showCarModal, setShowCarModal] = useState(false); const [editingCar, setEditingCar] = useState(null);
  const [inspectionData, setInspectionData] = useState(null);
  const [damageMarkers, setDamageMarkers] = useState([]);
  const [inspNotes, setInspNotes] = useState("");
  const [inspType, setInspType] = useState("pickup");
  const [viewDocs, setViewDocs] = useState(null); // {front, back} base64
  const navigate = useNavigate();
  const fetchData = useCallback(async () => {
    try {
      const [s, b, c, r, ct] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/bookings`),
        axios.get(`${API_URL}/api/cars`),
        axios.get(`${API_URL}/api/admin/reviews`),
        axios.get(`${API_URL}/api/admin/contracts`)
      ]);
      setStats(s.data); setBookings(b.data.bookings); setCars(c.data.cars); setReviews(r.data.reviews); setContracts(ct.data.contracts);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { if (!localStorage.getItem("admin_logged_in")) { navigate("/admin"); return; } fetchData(); }, [navigate, fetchData]);
  const handleLogout = () => { localStorage.removeItem("admin_logged_in"); localStorage.removeItem("admin_username"); navigate("/admin"); };
  const updateBookingStatus = async (id, status, ps = null) => { try { await axios.put(`${API_URL}/api/admin/bookings/${id}/status?status=${status}${ps ? `&payment_status=${ps}` : ""}`); fetchData(); } catch (e) { console.error(e); } };
  const deleteCar = async (id) => { if (!window.confirm("Supprimer ?")) return; try { await axios.delete(`${API_URL}/api/admin/cars/${id}`); fetchData(); } catch (e) { console.error(e); } };
  const approveReview = async (id) => { try { await axios.put(`${API_URL}/api/admin/reviews/${id}/approve`); fetchData(); } catch (e) { console.error(e); } };
  const deleteReview = async (id) => { if (!window.confirm("Supprimer cet avis ?")) return; try { await axios.delete(`${API_URL}/api/admin/reviews/${id}`); fetchData(); } catch (e) { console.error(e); } };
  const generateContract = async (bookingId) => { try { await axios.post(`${API_URL}/api/admin/contracts/generate/${bookingId}`); fetchData(); setActiveTab("contracts"); } catch (e) { console.error(e); } };
  const confirmContract = async (contractId) => { try { await axios.put(`${API_URL}/api/admin/contracts/${contractId}/confirm`); fetchData(); } catch (e) { console.error(e); } };
  const downloadPdf = (contractId) => { window.open(`${API_URL}/api/admin/contracts/${contractId}/pdf`, "_blank"); };
  const viewBookingDocs = async (bookingId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/bookings/${bookingId}/documents`);
      setViewDocs(res.data);
    } catch (e) { console.error(e); alert("Aucun document trouvé"); }
  };
  const handleCarDiagramClick = (e, view) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    const label = prompt("Description du dommage (ex: Rayure, Bosse...):");
    if (label) setDamageMarkers(prev => [...prev, { x, y, label, view }]);
  };
  const saveInspection = async () => {
    if (!inspectionData) return;
    try {
      await axios.post(`${API_URL}/api/admin/inspections`, { booking_id: inspectionData.booking_id, car_id: inspectionData.car_id, damage_markers: damageMarkers, notes: inspNotes, photos: [], inspection_type: inspType });
      setInspectionData(null); setDamageMarkers([]); setInspNotes(""); fetchData(); alert("Inspection sauvegardée !");
    } catch (e) { console.error(e); }
  };
  const getStatusBadge = (s) => { const c = { pending: "status-pending", confirmed: "status-confirmed", cancelled: "status-cancelled", completed: "status-completed" }; const l = { pending: "En attente", confirmed: "Confirmée", cancelled: "Annulée", completed: "Terminée" }; return <span className={`badge ${c[s]}`}>{l[s]}</span>; };

  const pendingReviewsCount = reviews.filter(r => !r.approved).length;

  return (
    <div className="min-h-screen bg-[var(--background)]" data-testid="admin-dashboard">
      <aside className="admin-sidebar hidden lg:block">
        <img src="https://static.prod-images.emergentagent.com/jobs/9fbc62be-9ca6-4a52-a262-7277632737f6/images/66bea74a5f4b92055b5bbbae51f5178c1fabf0694382f7474751b63e58c61714.png" alt="SIMCarRent" className="h-10 mb-8" />
        <nav className="space-y-2">
          {[
            { k: "dashboard", i: <Settings size={18} />, l: "Tableau de bord" },
            { k: "bookings", i: <Calendar size={18} />, l: "Réservations" },
            { k: "contracts", i: <FileText size={18} />, l: "Contrats" },
            { k: "cars", i: <Car size={18} />, l: "Véhicules" },
            { k: "reviews", i: <Star size={18} />, l: "Avis", badge: pendingReviewsCount },
          ].map(t => (
            <button key={t.k} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${activeTab === t.k ? "bg-[var(--primary)] text-white" : "text-gray-400 hover:bg-[var(--surface-elevated)]"}`} onClick={() => setActiveTab(t.k)} data-testid={`nav-${t.k}`}>
              {t.i} {t.l}
              {t.badge > 0 && <span className="ml-auto bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{t.badge}</span>}
            </button>
          ))}
        </nav>
        <div className="absolute bottom-6 left-6 right-6"><button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition" onClick={handleLogout} data-testid="logout-btn"><LogOut size={18} /> Déconnexion</button></div>
      </aside>
      <main className="lg:ml-[260px] p-6 lg:p-8">
        <div className="lg:hidden flex items-center justify-between mb-6">
          <img src="https://static.prod-images.emergentagent.com/jobs/9fbc62be-9ca6-4a52-a262-7277632737f6/images/66bea74a5f4b92055b5bbbae51f5178c1fabf0694382f7474751b63e58c61714.png" alt="SIMCarRent" className="h-8" />
          <div className="flex gap-2">
            {["dashboard","bookings","contracts","cars","reviews"].map(t => (
              <button key={t} className={`p-2 rounded-lg relative ${activeTab === t ? "bg-[var(--primary)]" : "bg-[var(--surface)]"}`} onClick={() => setActiveTab(t)}>
                {t === "dashboard" ? <Settings size={18}/> : t === "bookings" ? <Calendar size={18}/> : t === "contracts" ? <FileText size={18}/> : t === "cars" ? <Car size={18}/> : <Star size={18}/>}
                {t === "reviews" && pendingReviewsCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{pendingReviewsCount}</span>}
              </button>
            ))}
            <button className="p-2 rounded-lg bg-[var(--surface)] text-red-400" onClick={handleLogout}><LogOut size={18} /></button>
          </div>
        </div>

        {activeTab === "dashboard" && stats && (
          <div>
            <h1 className="text-2xl font-bold mb-6 text-white">Tableau de bord</h1>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              {[
                { l: "Véhicules", v: stats.total_cars, c: "text-white" },
                { l: "Disponibles", v: stats.available_cars, c: "text-green-500" },
                { l: "Réservations", v: stats.total_bookings, c: "text-white" },
                { l: "En attente", v: stats.pending_bookings, c: "text-yellow-500" },
                { l: "Avis en attente", v: stats.pending_reviews || 0, c: "text-orange-500" },
              ].map((s, i) => <div key={i} className="card p-6"><p className="text-sm text-gray-400 mb-1">{s.l}</p><p className={`text-3xl font-bold ${s.c}`}>{s.v}</p></div>)}
            </div>
            {stats.cars_needing_photos > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-8 flex items-center gap-3">
                <Image size={24} className="text-red-400 flex-shrink-0" />
                <div>
                  <p className="text-white font-bold">{stats.cars_needing_photos} véhicule(s) avec photos manquantes</p>
                  <p className="text-gray-400 text-sm">Allez dans l'onglet Véhicules et rechargez les photos. Les nouvelles photos sont sauvegardées de façon permanente.</p>
                </div>
                <button className="btn-primary text-sm py-2 px-4 flex-shrink-0" onClick={() => setActiveTab("cars")}>Corriger</button>
              </div>
            )}
            <div className="card p-6"><h2 className="font-bold mb-4 text-white">Dernières réservations</h2><div className="overflow-x-auto"><table className="table"><thead><tr><th>ID</th><th>Client</th><th>Véhicule</th><th>Dates</th><th>Statut</th></tr></thead><tbody>{bookings.slice(0,5).map(b => <tr key={b.id}><td className="font-mono text-[var(--primary)]">{b.booking_id}</td><td className="text-white">{b.first_name} {b.last_name}</td><td className="text-white">{b.car_info?.brand} {b.car_info?.model}</td><td className="text-gray-400">{b.start_date} → {b.end_date}</td><td>{getStatusBadge(b.status)}</td></tr>)}{bookings.length === 0 && <tr><td colSpan="5" className="text-center text-gray-400">Aucune réservation</td></tr>}</tbody></table></div></div>
          </div>
        )}

        {activeTab === "bookings" && (
          <div>
            <h1 className="text-2xl font-bold mb-6 text-white">Réservations</h1>
            <div className="card overflow-hidden"><div className="overflow-x-auto"><table className="table"><thead><tr><th>ID</th><th>Client</th><th>Contact</th><th>Véhicule</th><th>Dates</th><th>Lieu</th><th>Montant</th><th>Statut</th><th>Actions</th></tr></thead><tbody>
              {bookings.map(b => <tr key={b.id}><td className="font-mono text-[var(--primary)]">{b.booking_id}</td><td className="text-white">{b.first_name} {b.last_name}</td><td><div className="text-sm text-white">{b.phone}</div><div className="text-xs text-gray-400">{b.email}</div></td><td className="text-white">{b.car_info?.brand} {b.car_info?.model}</td><td className="text-sm text-gray-400">{b.start_date} → {b.end_date}</td><td className="text-sm text-gray-300">{b.pickup_display || "-"}</td><td><div className="text-white">{b.total_price?.toLocaleString()} FCFA</div><div className="text-xs text-gray-400">Dep: {b.deposit_amount?.toLocaleString()}</div></td><td>{getStatusBadge(b.status)}</td><td><div className="flex gap-1 flex-wrap">{b.status === "pending" && <><button className="p-2 rounded bg-green-500/20 text-green-500 hover:bg-green-500/30" onClick={() => updateBookingStatus(b.id, "confirmed", "deposit_paid")} title="Confirmer"><Check size={14}/></button><button className="p-2 rounded bg-red-500/20 text-red-500 hover:bg-red-500/30" onClick={() => updateBookingStatus(b.id, "cancelled")} title="Annuler"><X size={14}/></button></>}{b.status === "confirmed" && <button className="p-2 rounded bg-blue-500/20 text-blue-500 hover:bg-blue-500/30" onClick={() => updateBookingStatus(b.id, "completed", "fully_paid")} title="Terminée"><Check size={14}/></button>}<button className="p-2 rounded bg-[var(--primary)]/20 text-[var(--primary)] hover:bg-[var(--primary)]/30" onClick={() => generateContract(b.id)} title="Contrat"><FileText size={14}/></button><button className="p-2 rounded bg-purple-500/20 text-purple-400 hover:bg-purple-500/30" onClick={() => { setInspectionData({ booking_id: b.id, car_id: b.car_id }); setDamageMarkers([]); setInspNotes(""); setActiveTab("inspection"); }} title="Inspection"><ClipboardCheck size={14}/></button>{b.has_documents && <button className="p-2 rounded bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30" onClick={() => viewBookingDocs(b.id)} title="Documents"><Eye size={14}/></button>}</div></td></tr>)}
              {bookings.length === 0 && <tr><td colSpan="9" className="text-center text-gray-400">Aucune réservation</td></tr>}
            </tbody></table></div></div>
          </div>
        )}

        {activeTab === "contracts" && (
          <div>
            <h1 className="text-2xl font-bold mb-6 text-white">Contrats</h1>
            {contracts.length === 0 ? <p className="text-gray-400 text-center py-10">Aucun contrat. Générez un contrat depuis l'onglet Réservations.</p> : (
              <div className="space-y-4">
                {contracts.map(ct => (
                  <div key={ct.id} className="card p-5" data-testid={`contract-${ct.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-[var(--primary)] font-bold">{ct.booking_ref}</span>
                          <span className={`badge ${ct.status === "draft" ? "status-pending" : ct.status === "confirmed" ? "status-confirmed" : "badge-success"}`}>{ct.status === "draft" ? "Brouillon" : ct.status === "confirmed" ? "Confirmé" : "Signé"}</span>
                        </div>
                        <p className="text-white">{ct.client?.first_name} {ct.client?.last_name} — {ct.car?.brand} {ct.car?.model}</p>
                        <p className="text-sm text-gray-400">{ct.rental?.start_date} → {ct.rental?.end_date} • {ct.rental?.days}j • {ct.rental?.total_price?.toLocaleString()} FCFA</p>
                        <p className="text-sm text-gray-400">Lieu: {ct.rental?.pickup_display}</p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        {ct.status === "draft" && <button className="btn-primary text-sm py-2 px-4 flex items-center gap-1" onClick={() => confirmContract(ct.id)}><Check size={14} /> Confirmer</button>}
                        <button className="btn-secondary text-sm py-2 px-4 flex items-center gap-1" onClick={() => downloadPdf(ct.id)}><FileText size={14} /> PDF</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "inspection" && inspectionData && (
          <div>
            <div className="flex items-center gap-4 mb-6">
              <button onClick={() => { setActiveTab("bookings"); setInspectionData(null); }} className="text-gray-400 hover:text-white"><ArrowLeft size={20} /></button>
              <h1 className="text-2xl font-bold text-white">Inspection du véhicule</h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Car diagram */}
              <div>
                <div className="flex gap-3 mb-4">
                  <button className={`px-4 py-2 rounded-lg text-sm font-medium ${inspType === "pickup" ? "bg-[var(--primary)] text-white" : "bg-[var(--surface)] text-gray-400"}`} onClick={() => setInspType("pickup")}>Remise (pickup)</button>
                  <button className={`px-4 py-2 rounded-lg text-sm font-medium ${inspType === "return" ? "bg-[var(--primary)] text-white" : "bg-[var(--surface)] text-gray-400"}`} onClick={() => setInspType("return")}>Retour</button>
                </div>

                <p className="text-gray-400 text-sm mb-3">Cliquez sur le schéma pour marquer les dommages</p>

                {/* Top view */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Vue du dessus</p>
                  <div className="relative bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4 cursor-crosshair" style={{height: 200}} onClick={(e) => handleCarDiagramClick(e, "top")} data-testid="car-diagram-top">
                    <svg viewBox="0 0 300 150" className="w-full h-full opacity-30">
                      <rect x="60" y="10" width="180" height="130" rx="30" fill="none" stroke="#FF5E00" strokeWidth="2"/>
                      <rect x="80" y="25" width="60" height="40" rx="5" fill="none" stroke="#FF5E00" strokeWidth="1" opacity="0.5"/>
                      <rect x="160" y="25" width="60" height="40" rx="5" fill="none" stroke="#FF5E00" strokeWidth="1" opacity="0.5"/>
                      <rect x="80" y="85" width="60" height="40" rx="5" fill="none" stroke="#FF5E00" strokeWidth="1" opacity="0.5"/>
                      <rect x="160" y="85" width="60" height="40" rx="5" fill="none" stroke="#FF5E00" strokeWidth="1" opacity="0.5"/>
                      <circle cx="55" cy="40" r="12" fill="none" stroke="#FF5E00" strokeWidth="1.5"/>
                      <circle cx="245" cy="40" r="12" fill="none" stroke="#FF5E00" strokeWidth="1.5"/>
                      <circle cx="55" cy="110" r="12" fill="none" stroke="#FF5E00" strokeWidth="1.5"/>
                      <circle cx="245" cy="110" r="12" fill="none" stroke="#FF5E00" strokeWidth="1.5"/>
                    </svg>
                    {damageMarkers.filter(m => m.view === "top").map((m, i) => (
                      <div key={i} className="absolute w-5 h-5 bg-red-500 rounded-full border-2 border-white flex items-center justify-center text-[8px] text-white font-bold cursor-pointer" style={{left: `${m.x}%`, top: `${m.y}%`, transform: "translate(-50%,-50%)"}} title={m.label} onClick={(e) => { e.stopPropagation(); setDamageMarkers(prev => prev.filter((_, j) => j !== damageMarkers.indexOf(m))); }}>
                        {i+1}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Side view */}
                <div>
                  <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Vue latérale</p>
                  <div className="relative bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4 cursor-crosshair" style={{height: 150}} onClick={(e) => handleCarDiagramClick(e, "side")} data-testid="car-diagram-side">
                    <svg viewBox="0 0 300 100" className="w-full h-full opacity-30">
                      <path d="M40 70 L60 70 L80 40 L200 35 L240 55 L270 60 L270 70 L40 70Z" fill="none" stroke="#FF5E00" strokeWidth="2"/>
                      <circle cx="80" cy="75" r="15" fill="none" stroke="#FF5E00" strokeWidth="2"/>
                      <circle cx="230" cy="75" r="15" fill="none" stroke="#FF5E00" strokeWidth="2"/>
                      <rect x="90" y="40" width="50" height="25" rx="3" fill="none" stroke="#FF5E00" strokeWidth="1" opacity="0.5"/>
                      <rect x="150" y="38" width="50" height="25" rx="3" fill="none" stroke="#FF5E00" strokeWidth="1" opacity="0.5"/>
                    </svg>
                    {damageMarkers.filter(m => m.view === "side").map((m, i) => (
                      <div key={i} className="absolute w-5 h-5 bg-red-500 rounded-full border-2 border-white flex items-center justify-center text-[8px] text-white font-bold cursor-pointer" style={{left: `${m.x}%`, top: `${m.y}%`, transform: "translate(-50%,-50%)"}} title={m.label} onClick={(e) => { e.stopPropagation(); setDamageMarkers(prev => prev.filter((_, j) => j !== damageMarkers.indexOf(m))); }}>
                        {damageMarkers.filter(dm => dm.view === "top").length + i + 1}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Damage list + notes */}
              <div>
                <h3 className="font-bold text-white mb-3">Dommages signalés ({damageMarkers.length})</h3>
                {damageMarkers.length === 0 ? <p className="text-gray-400 text-sm mb-4">Aucun dommage signalé. Cliquez sur le schéma.</p> : (
                  <div className="space-y-2 mb-4">
                    {damageMarkers.map((m, i) => (
                      <div key={i} className="flex items-center gap-3 bg-[var(--surface)] p-3 rounded-lg border border-[var(--border)]">
                        <span className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-xs text-white font-bold">{i+1}</span>
                        <div className="flex-1">
                          <p className="text-white text-sm">{m.label}</p>
                          <p className="text-xs text-gray-400">Vue: {m.view === "top" ? "Dessus" : "Latérale"} ({m.x}%, {m.y}%)</p>
                        </div>
                        <button className="text-red-400 hover:text-red-300" onClick={() => setDamageMarkers(prev => prev.filter((_, j) => j !== i))}><X size={16} /></button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mb-4">
                  <label className="label">Notes d'inspection</label>
                  <textarea className="input min-h-[120px]" placeholder="Notes supplémentaires sur l'état du véhicule..." value={inspNotes} onChange={(e) => setInspNotes(e.target.value)} data-testid="inspection-notes" />
                </div>

                <button className="btn-primary w-full flex items-center justify-center gap-2" onClick={saveInspection} data-testid="save-inspection-btn">
                  <ClipboardCheck size={18} /> Sauvegarder l'inspection
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "cars" && (
          <div>
            <div className="flex justify-between items-center mb-6"><h1 className="text-2xl font-bold text-white">Véhicules</h1><button className="btn-primary flex items-center gap-2" onClick={() => { setEditingCar(null); setShowCarModal(true); }} data-testid="add-car-btn"><Plus size={18} /> Ajouter</button></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cars.map(car => (
                <div key={car.id} className="card">
                  <div className="h-40 bg-[var(--surface-elevated)] relative"><ImageGallerySmall images={car.images?.length > 0 ? car.images : (car.image_url ? [car.image_url] : [])} alt={`${car.brand} ${car.model}`} />{(car.images?.length || 0) > 1 && <span className="absolute top-2 left-2 z-10 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">{car.images.length} photos</span>}</div>
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2"><div><h3 className="font-bold text-white">{car.brand} {car.model}</h3><p className="text-sm text-gray-400">{car.year}</p></div><span className={`badge ${car.status === "available" ? "badge-success" : car.status === "rented" ? "status-pending" : "status-cancelled"}`}>{car.status === "available" ? "Dispo" : car.status === "rented" ? "Réservée" : "Maintenance"}</span></div>
                    <p className="price-tag text-lg mb-4">{car.daily_price?.toLocaleString()} FCFA/j</p>
                    <div className="flex gap-2"><button className="flex-1 btn-secondary flex items-center justify-center gap-2" onClick={() => { setEditingCar(car); setShowCarModal(true); }}><Edit size={14} /> Modifier</button><button className="p-2 rounded-lg bg-red-500/20 text-red-500 hover:bg-red-500/30" onClick={() => deleteCar(car.id)}><Trash2 size={14} /></button></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "reviews" && (
          <div>
            <h1 className="text-2xl font-bold mb-6 text-white">Gestion des avis</h1>
            <div className="space-y-4">
              {reviews.map(review => (
                <div key={review.id} className={`card p-5 ${!review.approved ? "border-l-4 border-l-yellow-500" : ""}`} data-testid={`admin-review-${review.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-[var(--primary)]/20 flex items-center justify-center text-[var(--primary)] font-bold text-sm">{review.name.charAt(0)}</div>
                        <div>
                          <p className="font-bold text-white">{review.name}</p>
                          <p className="text-xs text-gray-400">{review.location}</p>
                        </div>
                        <div className="flex gap-0.5 ml-2">{[...Array(review.stars)].map((_, j) => <Star key={j} size={14} className="text-yellow-500" fill="currentColor" />)}</div>
                        {!review.approved && <span className="badge status-pending ml-2">En attente</span>}
                        {review.approved && <span className="badge badge-success ml-2">Approuvé</span>}
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed">"{review.text}"</p>
                      <p className="text-xs text-gray-500 mt-2">{new Date(review.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</p>
                    </div>
                    <div className="flex gap-2 ml-4 flex-shrink-0">
                      {!review.approved && (
                        <button className="p-2 rounded bg-green-500/20 text-green-500 hover:bg-green-500/30" onClick={() => approveReview(review.id)} title="Approuver" data-testid={`approve-review-${review.id}`}>
                          <Check size={16} />
                        </button>
                      )}
                      <button className="p-2 rounded bg-red-500/20 text-red-500 hover:bg-red-500/30" onClick={() => deleteReview(review.id)} title="Supprimer" data-testid={`delete-review-${review.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {reviews.length === 0 && <p className="text-center text-gray-400 py-10">Aucun avis</p>}
            </div>
          </div>
        )}
      </main>
      {showCarModal && <CarModal car={editingCar} onClose={() => { setShowCarModal(false); setEditingCar(null); }} onSave={() => { setShowCarModal(false); setEditingCar(null); fetchData(); }} />}
      {viewDocs && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setViewDocs(null)}>
          <div className="glass rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Documents du client (Permis)</h3>
              <button onClick={() => setViewDocs(null)} className="p-2 hover:bg-[var(--surface)] rounded-lg"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="label mb-2">Recto (Avant)</p>
                {viewDocs.front ? (
                  viewDocs.front_type === "application/pdf" ? (
                    <a href={`data:application/pdf;base64,${viewDocs.front}`} download="permis_recto.pdf" className="btn-secondary w-full block text-center">Télécharger PDF (Recto)</a>
                  ) : (
                    <img src={`data:${viewDocs.front_type || "image/jpeg"};base64,${viewDocs.front}`} alt="Permis recto" className="w-full rounded-lg border border-[var(--border)]" />
                  )
                ) : (
                  <div className="w-full h-40 bg-[var(--surface)] rounded-lg flex items-center justify-center text-gray-400">Non fourni</div>
                )}
              </div>
              <div>
                <p className="label mb-2">Verso (Arrière)</p>
                {viewDocs.back ? (
                  viewDocs.back_type === "application/pdf" ? (
                    <a href={`data:application/pdf;base64,${viewDocs.back}`} download="permis_verso.pdf" className="btn-secondary w-full block text-center">Télécharger PDF (Verso)</a>
                  ) : (
                    <img src={`data:${viewDocs.back_type || "image/jpeg"};base64,${viewDocs.back}`} alt="Permis verso" className="w-full rounded-lg border border-[var(--border)]" />
                  )
                ) : (
                  <div className="w-full h-40 bg-[var(--surface)] rounded-lg flex items-center justify-center text-gray-400">Non fourni</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== APP ROUTER ====================

function App() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/vehicules" element={<VehiculesPage />} />
        <Route path="/comment-ca-marche" element={<CommentCaMarchePage />} />
        <Route path="/avis" element={<AvisPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/car/:carId" element={<CarDetailPage />} />
        <Route path="/admin" element={<AdminLoginPage />} />
        <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
      </Routes>
    </HashRouter>
  );
}

export default App;
